import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { getArticleBreadcrumb } from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-12 text-center">
          <h1 className="text-2xl font-bold text-foreground-900">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block cursor-pointer">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = getArticleBreadcrumb(article.categorySlug, article.title);

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '国产在线',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://guochanzx.com/article/${slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-12">
          <article className="max-w-[900px] mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <header className="mt-6 mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读约{article.readTime}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200">
                <h3 className="text-lg font-bold text-foreground-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group block bg-white rounded-lg border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer overflow-hidden"
                    >
                      <div className="w-full aspect-video overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-400 mt-1">{ra.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}